import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Clock, Play, Pause, Coffee, UtensilsCrossed, DollarSign, 
  TrendingUp, Users, Calendar, AlertCircle, CheckCircle2, Timer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user, api } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockStatus, setClockStatus] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateReason, setLateReason] = useState('');
  const [pendingPunchType, setPendingPunchType] = useState(null);
  const [trainingMode, setTrainingMode] = useState(false);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, payrollRes] = await Promise.all([
        api.get('/time/current-status'),
        api.get('/payroll/current')
      ]);
      setClockStatus(statusRes.data);
      setPayrollData(payrollRes.data);
      setTrainingMode(statusRes.data.training_mode || false);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handlePunch = async (punchType, reason = null) => {
    setLoading(true);
    try {
      await api.post('/time/punch', {
        punch_type: punchType,
        late_reason: reason,
        training_mode: trainingMode
      });
      
      const punchLabels = {
        shift_start: 'Shift Started',
        shift_end: 'Shift Ended',
        lunch_start: 'Lunch Started',
        lunch_end: 'Lunch Ended',
        break_start: 'Break Started',
        break_end: 'Break Ended'
      };
      
      toast.success(punchLabels[punchType] || 'Punch recorded');
      await fetchStatus();
    } catch (error) {
      if (error.response?.data?.detail?.includes('Reason for lateness')) {
        setPendingPunchType(punchType);
        setShowLateModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Punch failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLateSubmit = async () => {
    if (!lateReason.trim()) {
      toast.error('Please provide a reason for lateness');
      return;
    }
    setShowLateModal(false);
    await handlePunch(pendingPunchType, lateReason);
    setLateReason('');
    setPendingPunchType(null);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateDailyProgress = () => {
    if (!clockStatus?.current_worked_minutes) return 0;
    const targetMinutes = 8 * 60; // 8 hours
    return Math.min(100, (clockStatus.current_worked_minutes / targetMinutes) * 100);
  };

  const formatWorkedTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Welcome back, <span className="text-[#D4AF37]">{user?.full_name?.split(' ')[0]}</span>
            </h1>
            <p className="text-white/50 mt-1">{formatDate(currentTime)}</p>
          </div>
          
          {/* Training Mode Toggle */}
          <div className="flex items-center gap-3 glass-card px-4 py-2">
            <Label htmlFor="training-mode" className="text-white/70 text-sm">Training Mode</Label>
            <Switch
              id="training-mode"
              checked={trainingMode}
              onCheckedChange={setTrainingMode}
              className="data-[state=checked]:bg-[#D4AF37]"
              data-testid="toggle-training-mode"
            />
            {trainingMode && (
              <Badge className="bg-[#4B167A] text-white">No-Ding Active</Badge>
            )}
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cadence Clock Card */}
          <Card className="lg:col-span-2 glass-card border-0 overflow-hidden" data-testid="cadence-clock-card">
            <CardHeader className="border-b border-white/10 pb-4">
              <CardTitle className="flex items-center gap-2 text-white">
                <Clock className="w-5 h-5 text-[#D4AF37]" />
                Cadence Clock
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Digital Clock Display */}
              <div className="text-center mb-8">
                <div className="clock-display" data-testid="clock-display">
                  {formatTime(currentTime)}
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className={`status-dot ${clockStatus?.clocked_in ? 'status-online' : 'status-offline'}`} />
                  <span className="text-white/70">
                    {clockStatus?.clocked_in ? 'On Shift' : 'Off Shift'}
                    {clockStatus?.on_lunch && ' - Lunch'}
                    {clockStatus?.on_break && ' - Break'}
                  </span>
                </div>
              </div>

              {/* Punch Buttons Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Shift Start/End */}
                {!clockStatus?.clocked_in ? (
                  <Button
                    onClick={() => handlePunch('shift_start')}
                    disabled={loading}
                    className="col-span-2 md:col-span-1 h-16 bg-[#10B981] hover:bg-[#10B981]/80 text-white font-bold rounded-xl"
                    data-testid="btn-shift-start"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Start Shift
                  </Button>
                ) : (
                  <Button
                    onClick={() => handlePunch('shift_end')}
                    disabled={loading || clockStatus?.on_lunch || clockStatus?.on_break}
                    className="col-span-2 md:col-span-1 h-16 bg-[#EF4444] hover:bg-[#EF4444]/80 text-white font-bold rounded-xl"
                    data-testid="btn-shift-end"
                  >
                    <Pause className="w-5 h-5 mr-2" />
                    End Shift
                  </Button>
                )}

                {/* Lunch Button */}
                <Button
                  onClick={() => handlePunch(clockStatus?.on_lunch ? 'lunch_end' : 'lunch_start')}
                  disabled={loading || !clockStatus?.clocked_in || clockStatus?.on_break}
                  className={`h-16 font-bold rounded-xl ${
                    clockStatus?.on_lunch 
                      ? 'bg-[#F59E0B] hover:bg-[#F59E0B]/80' 
                      : 'bg-[#1F1B71] hover:bg-[#1F1B71]/80 border border-[#D4AF37]/30'
                  }`}
                  data-testid="btn-lunch"
                >
                  <UtensilsCrossed className="w-5 h-5 mr-2" />
                  {clockStatus?.on_lunch ? 'End Lunch' : 'Start Lunch'}
                </Button>

                {/* Break Button */}
                <Button
                  onClick={() => handlePunch(clockStatus?.on_break ? 'break_end' : 'break_start')}
                  disabled={loading || !clockStatus?.clocked_in || clockStatus?.on_lunch || (clockStatus?.break_count >= 2 && !clockStatus?.on_break)}
                  className={`h-16 font-bold rounded-xl ${
                    clockStatus?.on_break 
                      ? 'bg-[#F59E0B] hover:bg-[#F59E0B]/80' 
                      : 'bg-[#1F1B71] hover:bg-[#1F1B71]/80 border border-[#D4AF37]/30'
                  }`}
                  data-testid="btn-break"
                >
                  <Coffee className="w-5 h-5 mr-2" />
                  {clockStatus?.on_break ? 'End Break' : `Break (${clockStatus?.break_count || 0}/2)`}
                </Button>
              </div>

              {/* Daily Progress */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white/70 text-sm">Daily Progress</span>
                  <span className="text-[#D4AF37] font-mono">
                    {formatWorkedTime(clockStatus?.current_worked_minutes || 0)} / 8h
                  </span>
                </div>
                <Progress 
                  value={calculateDailyProgress()} 
                  className="h-3 bg-[#1F1B71]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Live Pay Gauge */}
          <Card className="glass-card border-0" data-testid="pay-gauge-card">
            <CardHeader className="border-b border-white/10 pb-4">
              <CardTitle className="flex items-center gap-2 text-white">
                <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                Live Pay Gauge
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <p className="text-white/50 text-sm mb-1">This Week's Earnings</p>
                <p className="text-4xl font-bold shimmer" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  ${payrollData?.gross_pay?.toFixed(2) || '0.00'}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Billable Hours</span>
                  <span className="text-white font-mono">{payrollData?.billable_hours?.toFixed(1) || 0}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Contract Rate</span>
                  <span className="text-[#D4AF37] font-mono">${payrollData?.contract_rate || user?.contract_rate || 15}/hr</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Lunch Deducted</span>
                  <span className="text-white font-mono">{payrollData?.lunch_hours?.toFixed(1) || 0}h</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="pay-gauge">
                  <div 
                    className="pay-gauge-fill"
                    style={{ width: `${Math.min(100, ((payrollData?.billable_hours || 0) / 40) * 100)}%` }}
                  />
                </div>
                <p className="text-center text-white/50 text-xs mt-2">
                  {payrollData?.billable_hours?.toFixed(1) || 0} / 40 weekly hours
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Status</p>
                  <p className="text-white font-semibold">
                    {clockStatus?.clocked_in ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Today</p>
                  <p className="text-white font-semibold font-mono">
                    {formatWorkedTime(clockStatus?.current_worked_minutes || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4B167A]/40 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Week Total</p>
                  <p className="text-white font-semibold font-mono">
                    {payrollData?.total_hours?.toFixed(1) || 0}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1F1B71]/60 flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Breaks Used</p>
                  <p className="text-white font-semibold">{clockStatus?.break_count || 0} / 2</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Late Reason Modal */}
      <Dialog open={showLateModal} onOpenChange={setShowLateModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#F59E0B]" />
              Late Clock-In Detected
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/70 mb-4">
              You are clocking in after your scheduled start time. Please provide a reason for your lateness.
            </p>
            <Label htmlFor="late-reason" className="text-white/70">Reason for Lateness</Label>
            <Textarea
              id="late-reason"
              value={lateReason}
              onChange={(e) => setLateReason(e.target.value)}
              placeholder="Please explain why you are late..."
              className="mt-2 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
              data-testid="input-late-reason"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowLateModal(false);
                setLateReason('');
                setPendingPunchType(null);
              }}
              className="text-white/70 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLateSubmit}
              className="bg-[#D4AF37] text-[#0A091E] hover:bg-[#D4AF37]/80"
              data-testid="btn-submit-late-reason"
            >
              Submit & Clock In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
